"use client";

import React from "react";
import { CircleCheck, Settings, CirclePlay, ChartBar, ArrowRight, MessageSquare, ListChecks, Mic, ChartPie, Target, TrendingUp, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const useCases = ["Medir la satisfacción del cliente tras una interacción o compra.", "Evaluar el desempeño de agentes o servicios telefónicos.", "Realizar estudios de mercado o campañas de opinión.", "Obtener retroalimentación para mejorar procesos internos."];
const surveyTypes = [{
  id: "item-1",
  title: "1. Encuestas estructuradas",
  description: "Preguntas fijas con opciones de respuesta (por ejemplo: escala de 1 a 5, sí/no). Son ideales para formularios rápidos y fáciles de analizar. Perfectas para medir satisfacción del cliente o calidad de atención."
}, {
  id: "item-2",
  title: "2. Encuestas semi-estructuradas",
  description: "Combinan preguntas cerradas con algunas abiertas. Permiten obtener datos cuantitativos y cualitativos. Muy útiles para evaluar experiencias más complejas o servicios técnicos."
}, {
  id: "item-3",
  title: "3. Encuestas no estructuradas",
  description: "Preguntas abiertas que permiten respuestas libres y detalladas. Se usan cuando se busca entender emociones, percepciones o sugerencias. Requieren más tiempo de análisis, pero ofrecen insights profundos."
}];
const workflowStages = [{
  id: 1,
  title: "1. Configuración",
  icon: Settings,
  color: "text-primary",
  bgColor: "bg-primary/10",
  borderColor: "border-primary/20",
  features: ["Definición de encuestas", "Asignación de contactos", "Configuración de colas"]
}, {
  id: 2,
  title: "2. Operación",
  icon: CirclePlay,
  color: "text-secondary",
  bgColor: "bg-secondary/10",
  borderColor: "border-secondary/20",
  features: ["Campañas automáticas", "Marcador automático", "Gestión manual"]
}, {
  id: 3,
  title: "3. Reportes y Análisis",
  icon: ChartBar,
  color: "text-chart-3",
  bgColor: "bg-chart-3/10",
  borderColor: "border-chart-3/20",
  features: ["Dashboard de KPIs", "Reportes exportables", "Análisis gráfico"]
}];
const benefits = [{
  title: "Rapidez en la recopilación",
  description: "Las campañas se ejecutan más rápido que medios tradicionales. Resultados disponibles en minutos.",
  icon: TrendingUp
}, {
  title: "Coste reducido",
  description: "Menor inversión comparado con entrevistas cara a cara. No requiere desplazamientos.",
  icon: ChartPie
}, {
  title: "Mayor sinceridad",
  description: "Al ser una interacción impersonal, los encuestados expresan opiniones honestas libremente.",
  icon: MessageSquare
}, {
  title: "Versatilidad de público",
  description: "Útil para clientes actuales o potenciales en estudios de satisfacción, opinión o mercado.",
  icon: Users
}];
const responseFormats = [{
  label: "Respuesta única",
  icon: CircleCheck
}, {
  label: "Respuesta múltiple",
  icon: ListChecks
}, {
  label: "Respuesta libre",
  icon: MessageSquare
}, {
  label: "Grabación de voz",
  icon: Mic
}];
export default function SurveyFeedbackCapabilities() {
  return <section id="surveyfeedbackcapabilities" className="bg-background py-10 md:py-14 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
          <Badge variant="outline" className="mb-4 text-primary border-primary/30">
            Módulo de Encuestas
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Capacidades Avanzadas de Retroalimentación
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Herramientas integrales para la configuración, operación y análisis de encuestas, 
            diseñadas para capturar la voz del cliente y evaluar el rendimiento operativo.
          </p>
        </div>

        {/* Process Overview */}
        <div className="mb-8 md:mb-10">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-semibold font-default text-foreground mb-2">
              Vista general del proceso
            </h3>
            <p className="text-muted-foreground font-sans">
              Flujo operativo desde la creación hasta el análisis de resultados
            </p>
          </div>
          
          <div className="relative flex flex-col md:flex-row items-center justify-center gap-6 md:gap-4 lg:gap-8">
            {workflowStages.map((stage, index) => <React.Fragment key={stage.id}>
                <Card data-index={index} className={`w-full md:w-1/3 relative z-10 border-2 ${stage.borderColor} shadow-md hover:shadow-lg transition-shadow`}>
                  <CardHeader className={`pb-4 border-b ${stage.borderColor} ${stage.bgColor} rounded-t-xl`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-background shadow-sm ${stage.color}`}>
                        <stage.icon className="size-6" />
                      </div>
                      <CardTitle className="text-lg">{stage.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ul className="space-y-3">
                      {stage.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-2 text-sm text-muted-foreground font-sans">
                          <CircleCheck className={`size-4 mt-0.5 shrink-0 ${stage.color}`} />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
                
                {/* Arrow connector */}
                {index < workflowStages.length - 1 && <div className="hidden md:flex items-center justify-center text-muted-foreground/50 z-0">
                    <ArrowRight className="size-8" />
                  </div>}
                {/* Arrow mobile */}
                {index < workflowStages.length - 1 && <div className="flex md:hidden items-center justify-center text-muted-foreground/50 py-2">
                    <ArrowRight className="size-6 rotate-90" />
                  </div>}
              </React.Fragment>)}
          </div>
        </div>

        <Separator className="my-8" />

        {/* Survey Types & Use Cases */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8 md:mb-10">
          
          {/* Survey Types Accordion */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
                <Target className="size-6 text-primary" />
                Tipos de Encuestas
              </h3>
              <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                {surveyTypes.map((type, index) => <AccordionItem key={type.id} value={type.id} data-index={index}>
                    <AccordionTrigger className="text-base font-semibold hover:text-primary transition-colors">
                      {type.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground font-sans leading-relaxed">
                      {type.description}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {/* Response Formats */}
            <div>
              <h4 className="text-lg font-medium font-default text-foreground mb-4">
                Formatos de Respuesta Soportados
              </h4>
              <div className="flex flex-wrap gap-3">
                {responseFormats.map((format, index) => <Badge key={index} data-index={index} variant="secondary" className="px-3 py-1.5 text-sm bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 border border-secondary/20">
                    <format.icon className="size-4 mr-2" />
                    {format.label}
                  </Badge>)}
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div className="space-y-8">
            <Card className="bg-muted/50 border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <CircleCheck className="size-5 text-primary" />
                  ¿Para qué sirve?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {useCases.map((useCase, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                      <div className="size-1.5 rounded-lg bg-primary mt-2 shrink-0" />
                      <span className="text-muted-foreground font-sans">{useCase}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Benefits */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-2xl font-semibold font-default text-foreground mb-2">
              Beneficios Clave
            </h3>
            <p className="text-muted-foreground font-sans">
              Ventajas de implementar nuestro sistema de encuestas telefónicas
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => <Card key={index} data-index={index} className="border border-border shadow-sm hover:shadow-md transition-shadow bg-card">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <benefit.icon className="size-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

      </div>
    </section>;
}